import axios, { AxiosInstance , AxiosRequestConfig, ResponseType} from "axios";
import CONFIG from "../config/config";
import { isCentralizedJwtExpirationEnabled } from "@/data/constants/applicationFeatureFlags";

const TOKEN_KEY = "@BLUE-id_token";
export const JWT_EXPIRED_EVENT_NAME = "aipx:jwt-expired";

/**
 * Decode the JWT payload and check the `exp` claim. Treats malformed or
 * unparseable tokens as expired so a corrupt localStorage value triggers a
 * re-auth instead of letting `Bearer <garbage>` go to the server.
 *
 * Pure / synchronous - safe to call from `getToken()` on every request.
 * Only invoked when CENTRALIZED_JWT_EXPIRATION_ENABLED is on.
 */
export function isJwtExpired(token: string): boolean {
  try {
    const parts = token.split(".");
    if (parts.length < 2) return true;
    const payloadJson =
      typeof atob === "function"
        ? atob(parts[1])
        : Buffer.from(parts[1], "base64").toString("utf8");
    const { exp } = JSON.parse(payloadJson) as { exp?: unknown };
    if (typeof exp !== "number") return true;
    return Date.now() / 1000 >= exp;
  } catch {
    return true;
  }
}

function emitJwtExpired(reason: "exp" | "401"): void {
  if (typeof window === "undefined") return;
  try {
    window.dispatchEvent(
      new CustomEvent(JWT_EXPIRED_EVENT_NAME, { detail: { reason } })
    );
  } catch {
    // CustomEvent is universally available in modern browsers; the catch
    // exists purely as a guard against very old test environments.
  }
}

let token = "";
export const getToken = (): string | null => {
  if (typeof localStorage === "undefined") return null;
  const stored = localStorage.getItem(TOKEN_KEY);
  if (!stored) return null;

  // Proactive expiry check - only when the centralized handling flag is ON,
  // so the OFF-state behavior is byte-for-byte the legacy code path.
  if (isCentralizedJwtExpirationEnabled() && isJwtExpired(stored)) {
    try {
      localStorage.removeItem(TOKEN_KEY);
    } catch {
      // ignore - removal failure is non-fatal; we still emit the event
    }
    emitJwtExpired("exp");
    return null;
  }

  return stored;
};

export const createAxiosInstance = (method: string, baseUrl = CONFIG.api, timeout = 30000): AxiosInstance => {
  const token = getToken();
  const axiosOptions = {
    baseURL: baseUrl,
    timeout,
    method,
  };
  const instance = axios.create(axiosOptions);
  instance.interceptors.request.use(function (config) {
    config.headers.Authorization = `Bearer ${token}`;
    return config;
  });

  // Centralized JWT-expiration response interceptor. Gated behind the
  // app feature flag so prod ships with no behavioral change until the
  // flag is flipped (see APPLICATION_FEATURE_FLAGS).
  if (isCentralizedJwtExpirationEnabled()) {
    instance.interceptors.response.use(
      (r) => r,
      (err) => {
        const status = err?.response?.status;
        const body = err?.response?.data;
        const msg =
          typeof body?.error === "string" ? body.error.toLowerCase() : "";
        const isJwtAuthFailure =
          status === 401 &&
          (msg.includes("authorization error:") ||
            msg.includes("authentication error:"));
        if (isJwtAuthFailure) {
          if (typeof localStorage !== "undefined") {
            try { localStorage.removeItem(TOKEN_KEY); } catch {}
          }
          emitJwtExpired("401");
        }
        // Always re-reject so existing try/catch blocks behave unchanged.
        return Promise.reject(err);
      }
    );
  }

  return instance;
};
export  const apiCallWithSignal = async <T = any>(
  method: string,
  path: string,
  data?: any,
  signal?: AbortSignal,
  baseUrl?: string
): Promise<T> => {
  const config = {
    data,
    signal
  }
  const axiosInstance = createAxiosInstance(method, baseUrl);
  return axiosInstance<T>(path, config) as T;
};
const apiCall = async <T = any>(
  method: string,
  path: string,
  data: any = {},
  baseUrl?: string,
  timeout?: number,
  options?: { headers?: Record<string, string> }
): Promise<T> => {
  const axiosInstance = createAxiosInstance(method, baseUrl, timeout);
  const config: { data: any; headers?: Record<string, string> } = { data };
  if (options?.headers) config.headers = options.headers;
  return axiosInstance<T>(path, config) as T;
};
//tried keeping apiCall structure in order to match our token passing strategy
export const streamCall = async (
  method: string,
  path: string,
  data: any = {},
  baseUrl?: string,
  timeout?: number
) => {
  const axiosInstance: AxiosInstance = createAxiosInstance(method, baseUrl, timeout);
  debugger;
  //Logs the request details
  console.log("[streamCall] →", { method, path, timeout });
   // Use responseType 'arraybuffer' to handle binary data
  const res = await axiosInstance(path, {
    method,
    data,
    responseType: "arraybuffer",
    maxBodyLength: Infinity,
    maxContentLength: Infinity,
    validateStatus: () => true,
    timeout: timeout ?? 0,
  });
  const ab = res.data as ArrayBuffer;
  // Log response details
  console.log("[streamCall]", {
    status: res.status,
    contentType: res.headers?.["content-type"],
    size: ab?.byteLength ?? 0,
  });

  // Only for debugging: log first 16 bytes as hex
  if (ab && ab.byteLength >= 16) {
    const view = new Uint8Array(ab, 0, 16);
    const hex = Array.from(view).map(b => b.toString(16).padStart(2, "0")).join(" ");
    console.log("[streamCall] first16:", hex);
  }

  return res;
};

export const maybeSetToken = (_token: any, force = false) => {
  if (!token && _token) {
    token = _token;
  } else if (!token && !_token) {
    if (typeof localStorage !== "undefined") {
      const id_token = localStorage.getItem(TOKEN_KEY);
      if (id_token) token = id_token;
    }
  }
  if (force) token = _token;
};
export default apiCall;
